// Buttons. Web heights: chip 36 · line 40 · soft 40 · primary 40 (52 in panel
// footers) · small 36 (40 px hit area via CSS slop, HANDOFF §11); ≥48 on
// phones, ≥40 on tablets (CSS). Coral exists ONLY as
// <PrimaryAction> — exactly one per screen (rule 2); every other button is
// neutral (line / soft / ghost / ink / danger-text).

import { forwardRef, useEffect, useId, type ButtonHTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { coralStore, overlayStore, useOverlayDepth } from '@/app/overlay-store';
import { useSyncExternalStore } from 'react';
import { BrandMark } from './BrandMark';

export type ButtonVariant = 'line' | 'soft' | 'ghost' | 'ink' | 'danger' | 'teal-line';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  /** Stretch to the container width. */
  block?: boolean;
  /** Shows the pulsing dot (PrimaryAction: the pulsing mark) and disables the button. */
  loading?: boolean;
  children?: ReactNode;
}

function classes(base: string, variant: string, size: ButtonSize, block?: boolean, extra?: string) {
  return [base, `e-btn--${variant}`, `e-btn--${size}`, block ? 'e-btn--block' : '', extra ?? ''].filter(Boolean).join(' ');
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'line', size = 'md', icon: Icon, block, loading, children, className, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={classes('e-btn', variant, size, block, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="e-pulse" aria-hidden="true" /> : Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </button>
  );
});

/** 36 px row/table action (e.g. «تأكيد», «إنهاء» inline); 40 px hit area on desktop, 48 on phones. */
export const SmallButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, 'size'>>(function SmallButton(props, ref) {
  return <Button ref={ref} size="sm" variant={props.variant ?? 'line'} {...props} />;
});

export interface PrimaryActionProps extends Omit<ButtonProps, 'variant'> {
  /** md = 40 (page head) · lg = 52 (panel footer / dialog). */
  size?: 'md' | 'lg';
}

/**
 * The screen's ONE decisive action (سند قبض, سند صرف, ترحيل القيد, احجز…):
 * coral gradient pill. While a side panel or dialog is open, only the
 * PrimaryAction inside the top overlay stays coral; the page's own renders
 * as a neutral soft button. Two live coral actions at one depth → dev warning.
 */
export const PrimaryAction = forwardRef<HTMLButtonElement, PrimaryActionProps>(function PrimaryAction(
  { size = 'md', icon: Icon, block, loading, children, className, disabled, type = 'button', ...rest },
  ref,
) {
  const id = useId();
  const depth = useOverlayDepth();
  const top = useSyncExternalStore(overlayStore.subscribe, overlayStore.depth, overlayStore.depth);
  const isCoral = depth === top;

  useEffect(() => {
    if (!isCoral) return;
    coralStore.register(id, depth);
    return () => coralStore.unregister(id);
  }, [id, depth, isCoral]);

  return (
    <button
      ref={ref}
      type={type}
      className={classes('e-btn', isCoral ? 'primary' : 'soft', size, block, className)}
      data-primary="true"
      data-coral={isCoral ? 'true' : 'false'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        // Board 21 «نبض»: a 16–18 px mark fading to 45 % and back replaces the spinner.
        <BrandMark mode="pulse" size={size === 'lg' ? 18 : 16} tone={isCoral ? 'white' : 'brand'} />
      ) : Icon ? (
        <Icon aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
});
